import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Backdrop,
  CircularProgress,
  IconButton,
  Modal,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  MenuItem,
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

import { CloseIcon, ExclamationMarkCircleIcon, UserIcon } from '@icons';
import UserAvatar from '@components/UserAvatar';

import { ProfileModalMode } from './context';
import { ProfileImageError } from './ProfileCompletionModal';
import { PROFILE_IMAGE_ACCEPT } from './helpers';
import { useStyles } from './styles';

interface IProfileCompletionModalViewProps {
  open: boolean;
  mode: ProfileModalMode;
  name: string;
  gender: string;
  profileImageUrl?: string;
  onSubmit: () => void;
  onClose: () => void;
  onNameChange: (value: string) => void;
  onGenderChange: (value: string) => void;
  onImagePick: (file: File) => void;
  onSave: () => void;
  canSave: boolean;
  isSubmitting: boolean;
  isUploading: boolean;
  isSaving: boolean;
  saveFailed: boolean;
  imageError: ProfileImageError | null;
  languageOptions?: Array<{ key: string; label: string }>;
  language?: string;
  onLanguageChange?: (lang: string) => void;
}

/**
 * Renders the profile modal in either of its two modes.
 *
 * @param props.open - Whether the modal is mounted open.
 * @param props.mode - `required` renders the blocking first-login form with a
 * Continue button; `edit` renders the dismissible editor with Save and Cancel.
 * @param props.name - Current value of the name field.
 * @param props.gender - Current value of the gender radio group.
 * @param props.profileImageUrl - Photo shown above the form in edit mode: the
 * picked one while it is still unsaved, the stored one otherwise.
 * @param props.onSubmit - Submits the required-mode form.
 * @param props.onClose - Dismisses the modal, dropping unsaved edits; wired up
 * in edit mode only, and shared by the close button and Cancel.
 * @param props.onNameChange - Called on every keystroke in the name field.
 * @param props.onGenderChange - Called when a gender is picked.
 * @param props.onImagePick - Called with the file picked in the input or
 * dropped on the avatar; the file itself is validated upstream.
 * @param props.onSave - Writes the edited fields.
 * @param props.canSave - Whether there is a valid edit left to write.
 * @param props.isSubmitting - Disables the required-mode Continue button.
 * @param props.isUploading - Shows the spinner over the avatar's upload button.
 * @param props.isSaving - Shows the spinner inside the Save button.
 * @param props.saveFailed - Shows the marker left by a failed write.
 * @param props.imageError - Why the last picked picture was refused, if it was.
 * @param props.languageOptions - Assistant languages; hidden in edit mode.
 * @param props.language - Currently selected assistant language.
 * @param props.onLanguageChange - Called when an assistant language is picked.
 */
const ProfileCompletionModalView = ({
  open,
  mode,
  name,
  gender,
  profileImageUrl,
  onSubmit,
  onClose,
  onNameChange,
  onGenderChange,
  onImagePick,
  onSave,
  canSave,
  isSubmitting,
  isUploading,
  isSaving,
  saveFailed,
  imageError,
  languageOptions,
  language,
  onLanguageChange,
}: IProfileCompletionModalViewProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const isEditMode = mode === 'edit';

  const isFormValid =
    name.trim().length > 0 && (gender === 'male' || gender === 'female');

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again still fires a change event.
    event.target.value = '';

    if (file) {
      onImagePick(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    // Without this the browser navigates to the dropped file instead.
    event.preventDefault();

    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    // Ignored while the pointer only crossed into a child of the block.
    if (event.currentTarget.contains(event.relatedTarget as Node)) {
      return;
    }

    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];

    if (file) {
      onImagePick(file);
    }
  };

  return (
    <Modal
      sx={classes.root}
      open={open}
      components={{ Backdrop }}
      {...(isEditMode ? { onClose } : {})}
    >
      <Box
        component="div"
        sx={classes.container}
        {...(isEditMode ? { 'aria-label': t('userProfile.edit') } : {})}
      >
        {isEditMode ? (
          <>
            <IconButton
              sx={classes.closeButton}
              onClick={onClose}
              aria-label="close"
              data-testid="profile-completion-close-button"
            >
              <CloseIcon sx={classes.closeIcon} />
            </IconButton>

            <Box
              sx={[
                classes.avatarWrapper,
                isDragging && classes.avatarWrapperDragging,
              ]}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid="profile-completion-avatar-dropzone"
            >
              <UserAvatar src={profileImageUrl} name={name} />
              <IconButton
                sx={classes.avatarUploadButton}
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                aria-label={t('userProfile.upload')}
                data-testid="profile-completion-avatar-upload-button"
              >
                {isUploading ? (
                  <CircularProgress
                    size={14}
                    sx={classes.avatarUploadSpinner}
                    data-testid="profile-completion-uploading"
                  />
                ) : (
                  <FileUploadOutlinedIcon sx={classes.avatarUploadIcon} />
                )}
              </IconButton>
              <input
                hidden
                ref={fileInputRef}
                type="file"
                accept={PROFILE_IMAGE_ACCEPT}
                onChange={handleFileSelected}
                data-testid="profile-completion-avatar-input"
              />
            </Box>
          </>
        ) : (
          <Box component="div" sx={classes.iconContainer}>
            <UserIcon />
          </Box>
        )}

        {!isEditMode && (
          <>
            <Box sx={classes.titleRow}>
              <Typography sx={classes.title}>
                {t('profileCompletion.title')}
              </Typography>
            </Box>
            <Typography sx={classes.subtitle}>
              {t('profileCompletion.subtitle')}
            </Typography>
          </>
        )}

        <Box sx={classes.form}>
          <TextField
            fullWidth
            label={t('profileCompletion.nameLabel')}
            placeholder={t('profileCompletion.namePlaceholder')}
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            sx={classes.input}
            inputProps={{
              'aria-label': 'name',
              'data-testid': 'profile-completion-name-input',
            }}
          />

          <FormControl component="fieldset" sx={classes.formControl}>
            <Typography component="label" sx={classes.genderLabel}>
              {t('profileCompletion.genderLabel')}
            </Typography>
            <RadioGroup
              row
              value={gender}
              onChange={(e) => onGenderChange(e.target.value)}
              sx={classes.radioGroup}
            >
              <FormControlLabel
                value="male"
                control={<Radio sx={classes.radio} />}
                label={t('profileCompletion.male')}
              />
              <FormControlLabel
                value="female"
                control={<Radio sx={classes.radio} />}
                label={t('profileCompletion.female')}
              />
            </RadioGroup>
          </FormControl>

          {!isEditMode && languageOptions?.length && onLanguageChange ? (
            <TextField
              select
              fullWidth
              label={t('settingsModal.language')}
              value={language ?? ''}
              onChange={(e) => onLanguageChange(e.target.value)}
              sx={classes.input}
            >
              {languageOptions.map(({ key, label }) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          {isEditMode && imageError && (
            <Typography
              sx={classes.imageErrorText}
              data-testid="profile-completion-image-error"
            >
              {imageError === 'unsupported'
                ? t(
                    'userProfile.imageUnsupported',
                    'Use a PNG, JPEG or WebP image.',
                  )
                : t(
                    'userProfile.imageUploadFailed',
                    'Upload failed. Try again.',
                  )}
            </Typography>
          )}

          {isEditMode && !imageError && saveFailed && (
            <Box sx={classes.saveErrorRow}>
              <ExclamationMarkCircleIcon sx={classes.saveErrorIcon} />
            </Box>
          )}

          {isEditMode ? (
            <Box sx={classes.actions}>
              <Button
                sx={classes.secondaryButton}
                onClick={onClose}
                data-testid="profile-completion-cancel-button"
              >
                {t('userProfile.cancel')}
              </Button>
              <Button
                sx={classes.button}
                onClick={onSave}
                disabled={!canSave}
                data-testid="profile-completion-save-button"
              >
                {isSaving ? (
                  <CircularProgress
                    size={16}
                    sx={classes.savingIndicator}
                    data-testid="profile-completion-saving"
                  />
                ) : (
                  t('userProfile.save')
                )}
              </Button>
            </Box>
          ) : (
            <Button
              sx={classes.button}
              onClick={onSubmit}
              disabled={!isFormValid || isSubmitting}
            >
              {t('profileCompletion.submit')}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ProfileCompletionModalView;
